// NETSOL-12075 item #1: "Export to PDF" for the resident summary drill-down.
//
// Like exportMdsSummaryPdf.ts, this avoids a PDF dependency (the corporate npm
// registry blocks jsPDF) and the shadow-DOM caveats of printing in place: it
// clones the rendered detail into a hidden iframe on the *top* document, carries
// over the widget's stylesheets so the clone keeps its styling, and invokes the
// browser's native print dialog where the user picks "Save as PDF".

/** Serialize every reachable CSS rule from a document or shadow root. */
function collectCss(root: Document | ShadowRoot): string {
  let css = "";
  const sheets = (root as Document).styleSheets;
  if (sheets) {
    for (const sheet of Array.from(sheets)) {
      try {
        css += Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join("\n");
        css += "\n";
      } catch {
        // Cross-origin stylesheet — cssRules is not readable; skip it.
      }
    }
  }
  // Constructable stylesheets (adoptedStyleSheets) — used by some shadow roots.
  const adopted = (root as unknown as { adoptedStyleSheets?: CSSStyleSheet[] })
    .adoptedStyleSheets;
  if (adopted) {
    for (const sheet of adopted) {
      try {
        css += Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join("\n");
        css += "\n";
      } catch {
        // ignore
      }
    }
  }
  return css;
}

/**
 * Render a DOM element into a hidden iframe (with the app's styles) and open the
 * browser print dialog (Save as PDF). Returns false when there is no DOM (SSR)
 * or the element is missing, so callers can no-op safely.
 */
export function printElementToPdf(el: HTMLElement | null, title: string): boolean {
  if (typeof document === "undefined" || !el) return false;

  // Styles can live in the widget's shadow root and/or the top document; gather
  // both so the clone renders the same as on screen.
  const root = el.getRootNode() as Document | ShadowRoot;
  let css = collectCss(document);
  if (root !== document) css += collectCss(root);

  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  // Print-time overrides: the detail is a height-constrained, scrolling flexbox
  // on screen; for print let it expand to its full content so nothing clips.
  const printOverrides = `
    @media print { body { margin: 10mm; } }
    html, body { background: #ffffff; height: auto !important; overflow: visible !important; }
    [data-pdf-print-root] { height: auto !important; max-height: none !important; overflow: visible !important; }
    [data-pdf-print-root] * { overflow: visible !important; max-height: none !important; }
  `;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return false;
  }

  doc.open();
  doc.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(
      title,
    )}</title><style>${css}</style><style>${printOverrides}</style></head>` +
      `<body><div data-pdf-print-root>${el.outerHTML}</div></body></html>`,
  );
  doc.close();

  const win = iframe.contentWindow;
  if (!win) {
    document.body.removeChild(iframe);
    return false;
  }

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };
  win.addEventListener("afterprint", cleanup);

  // A short delay lets the cloned stylesheet apply before the print snapshot.
  win.setTimeout(() => {
    win.focus();
    win.print();
  }, 150);

  // Backstop cleanup in case afterprint never fires (older Safari).
  setTimeout(cleanup, 60_000);

  return true;
}
